import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateEffect } from 'ahooks';
import type { GoogleCaptchaRef } from '@/components/biz/google-captcha';
import { ErrorCode } from '@/constants/error';
import { Flow } from '@/constants/flow';
import { useBizState } from './use-biz-state';
import { useCore } from './use-core';
import { useCoreRequest } from './use-core-request';
import { useFlow } from './use-flow';
import { useLidForm } from './use-lid-form';

interface FormValues {
  email: string;
  phone: string;
  areaCode: string;
}

export const useVerifyUsername = (
  type: 'reset' | 'recovery',
  recaptchaRef: React.RefObject<GoogleCaptchaRef | null>,
  recaptchaToken: string,
  recaptchaLoading: boolean,
) => {
  const { t } = useTranslation();
  const { setFlow } = useFlow();
  const { lenovoId, lang } = useCore();
  const [buttonLoading, setButtonLoading] = useState(false);
  const { authType, email, areaCode, phone, googleVersion, setBizState } = useBizState();
  const { control, setError, handleSubmit, setFocus, trigger, getValues } = useLidForm({
    defaultValues: { email, areaCode, phone },
  });
  const [flowName, setFlowName] = useState('verify username');

  const toggleAuthType = () => {
    const type = authType === 'email' ? 'phone' : 'email';
    setBizState({ authType: type });
    setTimeout(() => setFocus(type));
  };

  const mapper = {
    reset: {
      title: t('password_reset_title'),
      request: lenovoId.resetPasswordVerifyUserName,
      onSuccess: () => setFlow(Flow.RESET_PASSWORD_STEP_INPUT_CODE),
      onCustomError: () => setFlow(Flow.RESET_PASSWORD_STEP_INPUT_USERNAME),
    },
    recovery: {
      title: t('account_recovery_title'),
      request: lenovoId.recoveryVerifyUserName,
      onSuccess: () => setFlow(Flow.RECOVERY_STEP_INPUT_CODE),
      onCustomError: () => setFlow(Flow.RECOVERY_STEP_INPUT_USERNAME),
    },
  };

  const { title, request, onSuccess, onCustomError } = mapper[type];

  const { run, loading } = useCoreRequest(
    (params: FormValues) => {
      const userName = authType === 'email' ? params.email : params.phone;
      const areaCode = authType === 'phone' ? params.areaCode : '';
      setBizState({
        userName,
        areaCode: params.areaCode,
        email: params.email,
        phone: params.phone,
      });
      return request({
        userName,
        areaCode,
        lang,
        googleToken: recaptchaToken,
        googleVersion,
        bid: window.bid,
      });
    },
    {
      manual: true,
      onBefore: () => {
        setButtonLoading(true);
      },
      onSuccess: () => {
        setButtonLoading(false);
        onSuccess();
      },
      onError: (errorCode) => {
        setButtonLoading(false);
        if (flowName === 'verify username') {
          switch (errorCode) {
            case ErrorCode.UsernameNotExists:
              setError(authType, t('account_user_not_found_error'));
              recaptchaRef.current?.reload();
              return true;

            case ErrorCode.InvalidUsername:
              if (authType === 'email') setError('email', t('email_format_error'));
              else setError('phone', t('phone_format_error'));
              recaptchaRef.current?.reload();
              return true;
          }
        } else if (flowName === 'reset') {
          switch (errorCode) {
            case ErrorCode.UserFrozen:
              setFlow(Flow.LOGIN_STEP_DISABLE_RECOVERY);
              return true;
            case ErrorCode.UserDisabled:
              setFlow(Flow.LOGIN_STEP_DISABLE);
              return true;
            case ErrorCode.UsernameNotExists:
            case ErrorCode.InvalidUsername:
              onCustomError();
              return true;
          }
        } else {
          switch (errorCode) {
            case ErrorCode.UserDisabled:
              setFlow(Flow.LOGIN_STEP_DISABLE);
              return true;
            case ErrorCode.UsernameNotExists:
            case ErrorCode.InvalidUsername:
              onCustomError();
              return true;
          }
        }
      },
    },
  );

  const doVerifyUserName = (value: string) => {
    setFlowName(value);
    return run({
      email,
      phone,
      areaCode,
    });
  };

  useUpdateEffect(() => {
    if (!recaptchaLoading) {
      setButtonLoading(false);
    }
  }, [recaptchaLoading]);

  return {
    run,
    loading,
    title,
    authType,
    buttonLoading,
    control,
    getValues,
    trigger,
    toggleAuthType,
    handleSubmit,
    doVerifyUserName,
  };
};
